import type { ShadingSystemCatalogItem, WindowCatalogItem } from "../model/types";

export const CONTROL_LOGIC_SHADING_DEMAND = [
  "None",
  "No Shading",
  "Smart Shading",
  "Facade Shading",
  "always shading",
] as const;
export const CONTROL_LOGIC_WIND_RISK = [
  "None",
  "No Shading",
  "Smart Shading",
  "Facade Shading",
  "always shading",
] as const;

export const SHADING_LOCATION_NONE = "none";
export const SHADING_SYSTEM_TYPE_NONE = "none";

export const SHADING_LOCATIONS = [SHADING_LOCATION_NONE, "outside", "inside", "inbetween"] as const;

export const normalizeCatalogLocation = (location?: null | string) => {
  if (location === undefined || location === null || location === SHADING_LOCATION_NONE) return "";
  return location;
};

export const areShadingLocationsEqual = (left?: null | string, right?: null | string) =>
  normalizeCatalogLocation(left) === normalizeCatalogLocation(right);

const formatShadingLocationLabel = (location: string, noneLabel = SHADING_LOCATION_NONE) => {
  const normalized = normalizeCatalogLocation(location);
  return normalized === "" ? noneLabel : normalized;
};

export const getShadingLocationDisplayName = formatShadingLocationLabel;

export const isShadingLocationSelected = (location?: string) => Boolean(location);

export const normalizeShadingSystemTypeToForm = (value?: null | string) => {
  if (value == null || value.trim() === "") return SHADING_SYSTEM_TYPE_NONE;
  return value.trim();
};

export const isShadingSystemSelected = (shadingSystemType?: string) => {
  const normalized = normalizeShadingSystemTypeToForm(shadingSystemType);
  return normalized !== SHADING_SYSTEM_TYPE_NONE;
};

export const normalizeShadingLocationToForm = (location?: null | string): string => {
  if (location === undefined || location === null || location === "") return SHADING_LOCATION_NONE;
  return location;
};

export const normalizeShadingLocationForApi = (location: string): null | string => {
  return location === SHADING_LOCATION_NONE ? null : location;
};

export type WindowCatalogEntryKey = {
  windowTypeDbId: number;
  windowTypeName: string;
};

export const getFrameTypesForWindowType = (
  catalog: { frameTypeName: string; windowTypeName: string }[],
  windowTypeName: string
) => {
  if (!windowTypeName) return [];

  const matchedFrameTypes = [
    ...new Set(catalog.filter((item) => item.windowTypeName === windowTypeName).map((item) => item.frameTypeName)),
  ].filter(Boolean);

  if (matchedFrameTypes.length > 0) return matchedFrameTypes;

  return [...new Set(catalog.map((item) => item.frameTypeName))].filter(Boolean).sort();
};

export const findWindowCatalogItem = <T extends { frameTypeName: string; windowTypeName: string }>(
  catalog: T[],
  windowTypeName: string,
  frameTypeName: string
): T | undefined => {
  if (!windowTypeName || !frameTypeName) return undefined;

  return catalog.find((item) => item.windowTypeName === windowTypeName && item.frameTypeName === frameTypeName);
};

export const findWindowCatalogItemById = <T extends { id: number }>(
  catalog: T[],
  windowTypeDbId?: number
): T | undefined => (windowTypeDbId == null ? undefined : catalog.find((item) => item.id === windowTypeDbId));

export const resolveWindowCatalogEntry = (
  catalog: { frameTypeName: string; id: number; windowTypeName: string }[],
  group: { frameType?: string; windowTypeName?: string }
): undefined | WindowCatalogEntryKey => {
  if (!group.windowTypeName?.trim() || !group.frameType?.trim()) return undefined;

  const catalogItem = findWindowCatalogItem(catalog, group.windowTypeName, group.frameType);
  if (!catalogItem) return undefined;

  return {
    windowTypeDbId: catalogItem.id,
    windowTypeName: catalogItem.windowTypeName,
  };
};

export type ShadingCatalogMatchResult = {
  filterContext: ShadingFilterContext;
  items: ShadingSystemCatalogItem[];
};

export type ShadingFilterContext = {
  canFilterByFrameType: boolean;
  canFilterByLocation: boolean;
  canFilterByWindowType: boolean;
  showFrameTypeInLabels: boolean;
  showLocationInLabels: boolean;
  showWindowTypeInLabels: boolean;
};

export type ShadingSelectionContext = {
  frameType: string;
  shadingLocation: string;
  windowTypeName: string;
};

export type ShadingSystemTypeSuggestions = {
  displayOptions: string[];
  options: string[];
  useDisambiguatedLabels: boolean;
};

export const hasShadingSelectionPrerequisites = (formState: Partial<ShadingSelectionContext>) =>
  Boolean(
    formState.windowTypeName?.trim() &&
      formState.frameType?.trim() &&
      isShadingLocationSelected(formState.shadingLocation)
  );

export const getShadingLocationDrawerOptions = (
  shadingCatalog: ShadingSystemCatalogItem[],
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id" | "windowTypeName">[],
  formState: Pick<ShadingSelectionContext, "frameType" | "shadingLocation" | "windowTypeName">,
  noneLabel = SHADING_LOCATION_NONE
) => {
  const selectionContext: ShadingSelectionContext = {
    frameType: formState.frameType,
    shadingLocation: "",
    windowTypeName: formState.windowTypeName,
  };
  const filterContext = resolveShadingFilterContext(windowCatalog, selectionContext, shadingCatalog);
  let matchingItems = filterShadingCatalogItems(shadingCatalog, windowCatalog, selectionContext, filterContext);

  if (matchingItems.length === 0 && shadingCatalog.length > 0) {
    const hasArbitraryWindowAndFrame = !filterContext.canFilterByWindowType && !filterContext.canFilterByFrameType;

    if (hasArbitraryWindowAndFrame) {
      matchingItems = [...shadingCatalog];
    }
  }

  const availableLocations = new Set(matchingItems.map((item) => normalizeShadingLocationToForm(item.location)));

  if (isShadingLocationSelected(formState.shadingLocation)) {
    availableLocations.add(formState.shadingLocation);
  }

  const options = SHADING_LOCATIONS.filter((location) => availableLocations.has(location));

  return {
    displayOptions: options.map((location) => (location === SHADING_LOCATION_NONE ? noneLabel : location)),
    options: [...options],
  };
};

export const isShadingLocationInCatalog = (
  shadingLocation: string,
  shadingItems: Pick<ShadingSystemCatalogItem, "location">[]
) => shadingItems.some((item) => areShadingLocationsEqual(item.location, shadingLocation));

export const resolveShadingFilterContext = (
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id" | "windowTypeName">[],
  formState: ShadingSelectionContext,
  shadingItems: ShadingSystemCatalogItem[]
): ShadingFilterContext => {
  const windowTypeName = formState.windowTypeName.trim();
  const frameType = formState.frameType.trim();
  const locationSelected = isShadingLocationSelected(formState.shadingLocation);
  const shadingLocation = formState.shadingLocation;

  const canFilterByWindowType = Boolean(
    windowTypeName && windowCatalog.some((item) => item.windowTypeName === windowTypeName)
  );

  const canFilterByFrameType = Boolean(
    frameType &&
      windowCatalog.some(
        (item) => item.frameTypeName === frameType && (!canFilterByWindowType || item.windowTypeName === windowTypeName)
      )
  );

  const canFilterByLocation = Boolean(locationSelected && isShadingLocationInCatalog(shadingLocation, shadingItems));

  return {
    canFilterByFrameType,
    canFilterByLocation,
    canFilterByWindowType,
    showFrameTypeInLabels: Boolean(frameType && !canFilterByFrameType),
    showLocationInLabels: Boolean(locationSelected && !canFilterByLocation),
    showWindowTypeInLabels: Boolean(windowTypeName && !canFilterByWindowType),
  };
};

export const filterShadingCatalogItems = (
  items: ShadingSystemCatalogItem[],
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id" | "windowTypeName">[],
  formState: ShadingSelectionContext,
  filterContext: ShadingFilterContext
) => {
  let filtered = items;

  if (filterContext.canFilterByWindowType) {
    const matchingWindowTypeDbIds = new Set(
      windowCatalog.filter((item) => item.windowTypeName === formState.windowTypeName.trim()).map((item) => item.id)
    );

    filtered = filtered.filter((item) => matchingWindowTypeDbIds.has(item.windowTypeDbId));
  }

  if (filterContext.canFilterByFrameType) {
    filtered = filtered.filter((item) => {
      const windowItem = findWindowCatalogItemById(windowCatalog, item.windowTypeDbId);
      return windowItem?.frameTypeName === formState.frameType.trim();
    });
  }

  if (filterContext.canFilterByLocation) {
    filtered = filtered.filter((item) => areShadingLocationsEqual(item.location, formState.shadingLocation));
  }

  return filtered;
};

const buildFallbackShadingCatalogMatch = (items: ShadingSystemCatalogItem[]): ShadingCatalogMatchResult => ({
  filterContext: {
    canFilterByFrameType: false,
    canFilterByLocation: false,
    canFilterByWindowType: false,
    showFrameTypeInLabels: true,
    showLocationInLabels: true,
    showWindowTypeInLabels: true,
  },
  items: [...items],
});

export const getMatchingShadingCatalogItems = (
  items: ShadingSystemCatalogItem[],
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id" | "windowTypeName">[],
  formState: ShadingSelectionContext
): ShadingCatalogMatchResult => {
  const filterContext = resolveShadingFilterContext(windowCatalog, formState, items);
  const filteredItems = filterShadingCatalogItems(items, windowCatalog, formState, filterContext);

  if (filteredItems.length > 0 || items.length === 0) {
    return { filterContext, items: filteredItems };
  }

  return buildFallbackShadingCatalogMatch(items);
};

export const formatShadingCatalogItemLabel = (
  item: ShadingSystemCatalogItem,
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id">[],
  filterContext: ShadingFilterContext,
  noneLocationLabel = SHADING_LOCATION_NONE
) => {
  const parts: string[] = [];
  const frameTypeName = findWindowCatalogItemById(windowCatalog, item.windowTypeDbId)?.frameTypeName ?? "";

  if (filterContext.showWindowTypeInLabels) parts.push(item.windowTypeName);
  if (filterContext.showFrameTypeInLabels) parts.push(frameTypeName);
  if (filterContext.showLocationInLabels) parts.push(formatShadingLocationLabel(item.location, noneLocationLabel));

  if (parts.length === 0) return item.shadingSystemType;
  return `${item.shadingSystemType} (${parts.join(", ")})`;
};

const withNoneShadingSystemTypeOption = (suggestions: ShadingSystemTypeSuggestions): ShadingSystemTypeSuggestions => {
  if (suggestions.options.includes(SHADING_SYSTEM_TYPE_NONE)) return suggestions;

  return {
    ...suggestions,
    displayOptions: [SHADING_SYSTEM_TYPE_NONE, ...suggestions.displayOptions],
    options: [SHADING_SYSTEM_TYPE_NONE, ...suggestions.options],
  };
};

export const buildShadingSystemTypeSuggestions = (
  matchResult: ShadingCatalogMatchResult,
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id">[],
  noneLocationLabel = SHADING_LOCATION_NONE
): ShadingSystemTypeSuggestions => {
  const { filterContext, items } = matchResult;
  const needsDisambiguation =
    filterContext.showFrameTypeInLabels || filterContext.showLocationInLabels || filterContext.showWindowTypeInLabels;

  if (!needsDisambiguation) {
    const options = [...new Set(items.map((item) => item.shadingSystemType))].sort();
    return withNoneShadingSystemTypeOption({ displayOptions: options, options, useDisambiguatedLabels: false });
  }

  const options = items
    .map((item) => formatShadingCatalogItemLabel(item, windowCatalog, filterContext, noneLocationLabel))
    .sort();

  return withNoneShadingSystemTypeOption({ displayOptions: options, options, useDisambiguatedLabels: true });
};

export const getShadingSystemTypeFormDisplayValue = (
  shadingSystemType: string,
  suggestions: ShadingSystemTypeSuggestions,
  matchResult: ShadingCatalogMatchResult,
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id">[],
  formState: ShadingSelectionContext,
  noneLocationLabel = SHADING_LOCATION_NONE
): string => {
  if (!isShadingSystemSelected(shadingSystemType)) return SHADING_SYSTEM_TYPE_NONE;

  const normalizedType = shadingSystemType;

  if (suggestions.options.includes(normalizedType)) {
    return normalizedType;
  }

  if (!suggestions.useDisambiguatedLabels) {
    return normalizedType;
  }

  const matchingItems = matchResult.items.filter((item) => item.shadingSystemType === normalizedType);
  const itemByLocation = matchingItems.find((item) =>
    areShadingLocationsEqual(item.location, formState.shadingLocation)
  );
  const item = itemByLocation ?? matchingItems[0];

  if (!item) {
    return suggestions.options.find((option) => option.startsWith(`${normalizedType} (`)) ?? normalizedType;
  }

  return formatShadingCatalogItemLabel(item, windowCatalog, matchResult.filterContext, noneLocationLabel);
};

export const resolveShadingCatalogItemSelection = (
  selection: string,
  suggestions: ShadingSystemTypeSuggestions,
  matchResult: ShadingCatalogMatchResult,
  windowCatalog: Pick<WindowCatalogItem, "frameTypeName" | "id">[],
  noneLocationLabel = SHADING_LOCATION_NONE
): ShadingSystemCatalogItem | undefined => {
  if (!isShadingSystemSelected(selection)) return undefined;

  const { filterContext, items } = matchResult;

  if (suggestions.useDisambiguatedLabels) {
    return items.find(
      (item) => formatShadingCatalogItemLabel(item, windowCatalog, filterContext, noneLocationLabel) === selection
    );
  }

  return items.find((item) => item.shadingSystemType === selection);
};

export const filterShadingForWindowCatalogEntry = (
  items: ShadingSystemCatalogItem[],
  entry?: WindowCatalogEntryKey
) => {
  if (!entry) return [];

  return items.filter((item) => item.windowTypeDbId === entry.windowTypeDbId);
};

export const findShadingCatalogItem = (
  catalog: ShadingSystemCatalogItem[],
  shadingSystemType: string,
  entry?: WindowCatalogEntryKey,
  shadingLocation?: string
) =>
  catalog.find((item) => {
    if (item.shadingSystemType !== shadingSystemType) return false;
    if (
      shadingLocation !== undefined &&
      isShadingLocationSelected(shadingLocation) &&
      isShadingLocationInCatalog(shadingLocation, catalog) &&
      !areShadingLocationsEqual(item.location, shadingLocation)
    ) {
      return false;
    }
    if (entry && item.windowTypeDbId !== entry.windowTypeDbId) return false;
    return true;
  });
